import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { useGTMPageView } from "@/hooks/useGTMPageView";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { initAnalytics } from "@/lib/analytics";
import { GTM_ID, scheduleGoogleTags } from "@/lib/tag-loader";
import { loadGoogleAds, updateAdsConsent } from "@/lib/google-ads";
import {
  CONSENT_UPDATED_EVENT,
  hasAnalyticsConsent,
  hasMarketingConsent,
  resolveRegion,
} from "@/lib/consent";


import appCss from "../styles.css?url";

// Only the families and weights the site actually renders. Dropping the unused
// Space Grotesk / Oswald families and the extra weights cuts font payload sharply.
const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Source+Sans+3:wght@400;600&family=Inter:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&display=swap";

const WEBSITE_JSONLD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Design by Jaik",
  url: "https://jaikmarketing.com/",
});

const ORG_JSONLD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "jaik",
  url: "https://jaikmarketing.com/",
  logo: "https://jaikmarketing.com/jaik-logo.png",
  description:
    "Contractor web design and SEO for Seattle trades — roofing, remodeling, plumbing. Free mockup for King County businesses.",
  areaServed: "King County, Washington",
  founder: { "@type": "Person", name: "Jaik" },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-253-293-1550",
    contactType: "sales",
    areaServed: "US",
    availableLanguage: "English",
  },
});

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "google-site-verification", content: "2UhcmoGXGcwtJz-xd6CssyrgPiHbzT8qx79RtpdEIBA" },
      { name: "google-site-verification", content: "j0nUXShO6emPI_OhYsmA-GnIT8qd-xwfGOTAGnFiwN8" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://fonts.googleapis.com" },
      { rel: "dns-prefetch", href: "https://fonts.gstatic.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
      { rel: "preload", as: "style", href: FONTS_HREF },
      { rel: "stylesheet", href: FONTS_HREF },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
    scripts: [
      {
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',wait_for_update:500});",
      },
      { type: "application/ld+json", children: WEBSITE_JSONLD },
      { type: "application/ld+json", children: ORG_JSONLD },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: () => <NotFound />,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="gtm"
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function GTMRouteTracker() {
  useEffect(() => {
    let cancelled = false;

    // No banner: resolve the visitor's region first. Tags only load outside
    // consent-required regions, and only if the visitor has not opted out.
    resolveRegion().then(() => {
      if (cancelled) return;
      if (hasAnalyticsConsent()) scheduleGoogleTags(initAnalytics);
      if (hasMarketingConsent()) loadGoogleAds();
      else updateAdsConsent("denied");
    });

    const onConsent = () => {
      if (hasMarketingConsent()) loadGoogleAds();
      else updateAdsConsent("denied");
      if (hasAnalyticsConsent()) scheduleGoogleTags(initAnalytics);
    };

    window.addEventListener(CONSENT_UPDATED_EVENT, onConsent);
    return () => {
      cancelled = true;
      window.removeEventListener(CONSENT_UPDATED_EVENT, onConsent);
    };
  }, []);
  useGTMPageView();
  return null;
}


function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <GTMRouteTracker />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-agency-bg px-4">
      <div className="max-w-md text-center">
        <h1 className="mb-3 font-heading text-2xl font-semibold text-agency-text">
          This page didn't load
        </h1>
        <p className="mb-6 font-sans text-agency-text-muted">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            className="btn-jaik btn-jaik-primary"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a href="/" className="btn-jaik btn-jaik-secondary">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
